import React, { useEffect, useRef, useState } from 'react';

import type { IconName } from 'shared';

import type { LayoutColor } from '../../../types';
import BaseCell from '../BaseCell';
import { TABLE_CELL_FONT_SIZE } from '../../constants';
import Avatar from '../../../Avatar';
import Icon from '../../../Icon';
import Layout from '../../../Layout';
import ProductImg from '../../../ProductImg';
import LinkCell from '../../../Table/LinkCell';
import TableCellTooltip from '../../../TableCellTooltip';
import Text from '../../../Text';

export interface TextCellMenuOption {
  value: string;
  label: string;
}

/** Avatar size for avatar+text variant (UserCell, ClientCell) and dropdown trigger. */
const CELL_AVATAR_SIZE = 24;
/** Min height of dropdown trigger so all CellDropdowns match (Owner-with-avatar height: paddingY 6×2 + avatar 24 = 36). */
const DROPDOWN_TRIGGER_MIN_HEIGHT = 36;

const IMAGE_SIZE = 36;
const IMAGE_BORDER_RADIUS = 8;
const IMAGE_TEXT_GAP = 12;
const CELL_LABEL_TEXT_SIZE = 'body-sm';

export type TextCellAlign = 'start' | 'center' | 'end';

export interface TextCellProps {
  value: string | null;
  supportingText?: string | null;
  href?: string;

  /** When set, render an avatar leading the text (TextAvatarCell behavior). */
  avatarUrl?: string | null;
  defaultIcon?: IconName;
  /** When defaultIcon is set, color for the placeholder icon (e.g. gray-200 to match image placeholders). */
  defaultIconColor?: LayoutColor;

  /** When set, render an image leading the text with optional supporting line (TextImageCell behavior). */
  imageUrl?: string | null;
  imageAlt?: string;
  /** Icon in the ProductImg placeholder when there is no `imageUrl`. Defaults to ProductImg’s default. */
  imagePlaceholderIcon?: IconName;
  align?: TextCellAlign;

  /** When true, render truncated text with tooltip on overflow (TextSummaryCell behavior). */
  summary?: boolean;
  /** When summary is true, max lines to show before truncation. Default 2. */
  summaryMaxLines?: 1 | 2 | 3 | 4;

  /** When true, render only the dropdown trigger (bordered box + label + caret). Use with CellDropdown. Requires menuOptions. */
  dropdownTrigger?: boolean;
  /** Trigger style when dropdownTrigger. 'pill' = no border, body-sm, hover pill (white bg, 1000 radius). */
  dropdownTriggerVariant?: 'default' | 'pill';
  /** Options for label resolution when dropdownTrigger; also used by CellDropdown for menu items. */
  menuOptions?: TextCellMenuOption[];
  /** Placeholder when value is empty and dropdownTrigger. Default "Select…". */
  menuPlaceholder?: string;
  /** When dropdownTrigger, pass true when menu is open to show up caret. From CellDropdown/Menuable. */
  isOpen?: boolean;

  /** Inner horizontal padding in px. Default 0 so NewTable Row is the single source of padding. Override if used outside table. */
  padding?: number;

  /** When true, render the main label with semibold weight. */
  semibold?: boolean;
}

const DROPDOWN_TRIGGER_PLACEHOLDER = 'Select…';

const TextCell: React.FC<TextCellProps> = ({
  value,
  supportingText,
  href,
  avatarUrl,
  defaultIcon,
  defaultIconColor,
  imageUrl,
  imageAlt = '',
  imagePlaceholderIcon,
  align = 'start',
  summary = false,
  summaryMaxLines = 2,
  dropdownTrigger = false,
  dropdownTriggerVariant = 'default',
  menuOptions,
  menuPlaceholder = DROPDOWN_TRIGGER_PLACEHOLDER,
  isOpen = false,
  padding = 0,
  semibold = false
}) => {
  const isEmpty = value === null || value === undefined || value === '';
  const displayValue = isEmpty ? '—' : value;

  // Hooks must be called unconditionally at the top (before any early returns)
  const textRef = useRef<HTMLDivElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!summary) return;

    const wrapperEl = textRef.current;
    if (!wrapperEl) return;

    const measureEl = wrapperEl.querySelector('[data-text-cell-measure]') as HTMLElement | null;
    if (!measureEl) return;

    const checkTruncation = () => {
      // columns using this cell will need to have a width set for the truncation check to work.
      const visibleHeight = wrapperEl.clientHeight;
      const fullHeight = measureEl.scrollHeight;
      setIsTruncated(fullHeight > visibleHeight);
    };

    checkTruncation();
  }, [displayValue, summary]);

  useEffect(() => {
    setImageError(false);
  }, [imageUrl]);

  if (dropdownTrigger && menuOptions !== undefined) {
    const displayLabel =
      isEmpty || value === '' ? menuPlaceholder : (menuOptions.find(opt => opt.value === value)?.label ?? value);
    const isPill = dropdownTriggerVariant === 'pill';
    const textColor = isEmpty || value === '' ? 'gray-400' : 'gray-600';
    const showAvatar = avatarUrl !== undefined || defaultIcon !== undefined;
    return (
      <Layout padding={padding} width="100%">
        <Layout
          direction="row"
          gap={8}
          minHeight={DROPDOWN_TRIGGER_MIN_HEIGHT}
          color={isPill ? undefined : 'white'}
          borderColor={isPill ? undefined : 'gray-50'}
          borderWidth={isPill ? 0 : 1}
          borderRadius={isPill ? 1000 : 4}
          paddingLeft={showAvatar ? 8 : 12}
          paddingRight={10}
          paddingY={6}
          align="center"
          justify="center"
          {...(isPill ? { hoverColor: 'white' as const } : {})}
        >
          {showAvatar ? (
            <Avatar
              size={CELL_AVATAR_SIZE}
              avatarUrl={avatarUrl ?? undefined}
              borderWidth={0}
              defaultIcon={defaultIcon ?? 'avatar'}
              defaultIconColor={defaultIconColor}
            />
          ) : null}
          <Text size={isPill ? 'body-sm' : 'tag'} color={textColor} wrap={false} ellipsis>
            {displayLabel}
          </Text>
          <Layout marginLeft={4} display="inline-flex">
            <Icon name={isOpen ? 'upCaret' : 'downCaret'} color={textColor} size={16} />
          </Layout>
        </Layout>
      </Layout>
    );
  }

  // Summary variant (2-line truncated text with tooltip when truncated)
  if (summary) {
    const textColor = href ? 'link-palette' : 'gray-600';
    const textContent = (
      <div ref={textRef} style={{ position: 'relative' }}>
        <Text size={TABLE_CELL_FONT_SIZE} color={textColor} maxLines={summaryMaxLines}>
          {displayValue}
        </Text>
        <div
          data-text-cell-measure
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            visibility: 'hidden',
            pointerEvents: 'none'
          }}
        >
          <Text size={TABLE_CELL_FONT_SIZE} color={textColor}>
            {displayValue}
          </Text>
        </div>
      </div>
    );

    const showTooltip = isTruncated && displayValue !== '—';
    const inner = showTooltip ? (
      <TableCellTooltip text={displayValue as string}>{textContent}</TableCellTooltip>
    ) : (
      textContent
    );

    return (
      <Layout width="100%" minWidth={0}>
        {href ? (
          <LinkCell href={href} padding={0} color="link-palette">
            {inner}
          </LinkCell>
        ) : (
          inner
        )}
      </Layout>
    );
  }

  // Image + text variant (TextImageCell behavior). When imageAlt is set this is an image cell: show image or ProductImg placeholder when unavailable.
  const isImageCell = imageAlt ? true : imageUrl != null;
  if (isImageCell) {
    const color = isEmpty ? 'gray-100' : href ? 'link-palette' : 'gray-600';
    const textAlign = align === 'start' ? 'left' : align === 'end' ? 'right' : 'center';
    const justify = align === 'start' ? 'flex-start' : align === 'end' ? 'flex-end' : 'center';
    const isEnd = align === 'end';
    const showPlaceholder = imageUrl == null || imageError;

    const imageNode = (
      <Layout
        flexShrink={0}
        width={IMAGE_SIZE}
        height={IMAGE_SIZE}
        overflow="hidden"
        borderRadius={IMAGE_BORDER_RADIUS}
      >
        {showPlaceholder ? (
          <ProductImg
            size="sm"
            srcKey={null}
            placeholderIcon={imagePlaceholderIcon}
            alt={imageAlt || 'Product image unavailable'}
            style={{
              width: IMAGE_SIZE,
              height: IMAGE_SIZE,
              minWidth: IMAGE_SIZE,
              minHeight: IMAGE_SIZE,
              objectFit: 'contain',
              borderRadius: IMAGE_BORDER_RADIUS
            }}
          />
        ) : (
          <img
            src={imageUrl ?? ''}
            alt={imageAlt}
            style={{
              width: IMAGE_SIZE,
              height: IMAGE_SIZE,
              objectFit: 'cover'
            }}
            onError={() => setImageError(true)}
          />
        )}
      </Layout>
    );

    const textNode = (
      <Layout direction="column" gap={0} minWidth={0} overflow="hidden">
        <Text size={CELL_LABEL_TEXT_SIZE} color={color} align={textAlign} ellipsis semibold={semibold}>
          {displayValue}
        </Text>
        {supportingText ? (
          <Text size="body-xs" color="gray-400" align={textAlign} ellipsis>
            {supportingText}
          </Text>
        ) : null}
      </Layout>
    );

    const content = (
      <Layout direction="row" gap={IMAGE_TEXT_GAP} align="center" justify={justify} minWidth={0} width="100%">
        {isEnd ? (
          <>
            {textNode}
            {imageNode}
          </>
        ) : (
          <>
            {imageNode}
            {textNode}
          </>
        )}
      </Layout>
    );

    if (href) {
      return (
        <Layout padding={padding} width="100%">
          <LinkCell href={href} padding={0} color="link-palette">
            {content}
          </LinkCell>
        </Layout>
      );
    }

    return (
      <Layout padding={padding} width="100%">
        {content}
      </Layout>
    );
  }

  // Avatar + text variant (TextAvatarCell behavior)
  if (avatarUrl !== undefined || defaultIcon !== undefined) {
    const leadingContent = (
      <Avatar
        size={CELL_AVATAR_SIZE}
        avatarUrl={avatarUrl ?? undefined}
        borderWidth={0}
        defaultIcon={defaultIcon ?? 'avatar'}
        defaultIconColor={defaultIconColor}
      />
    );

    return (
      <Layout padding={padding} width="100%">
        <BaseCell
          value={value}
          leadingContent={leadingContent}
          supportingText={supportingText}
          href={href}
          align={align}
          semibold={semibold}
        />
      </Layout>
    );
  }

  // Default text-only cell
  return (
    <Layout padding={padding} width="100%">
      <BaseCell value={value} supportingText={supportingText} href={href} align={align} semibold={semibold} />
    </Layout>
  );
};

export default TextCell;
